import type { ExtendedExportSettings } from '@/types/export'
import type { RenderProgress } from '@/features/export/utils/client-renderer'
import {
  formatBytes,
  getAudioBitrateForQuality,
  getDefaultAudioCodec,
  getPreferredContainerForCodec,
  getSupportedCodecs,
  mapToClientSettings,
  selectFallbackVideoCodec,
  validateSettings,
} from '@/features/export/utils/client-renderer'
import { renderAudioOnly, renderComposition } from '@/features/export/utils/client-render-engine'
import { convertTimelineToComposition } from '@/features/export/utils/timeline-to-composition'
import { resolveMediaUrls } from '@/features/export/deps/media-library'
import { useTimelineStore } from '@/features/export/deps/timeline'
import { useProjectStore } from '@/features/export/deps/projects'
import { usePlaybackStore } from '@/shared/state/playback'
import { saveExportFile } from '@/infrastructure/storage'

export interface AssistantExportRequest {
  settings?: Partial<ExtendedExportSettings>
  signal?: AbortSignal
  download?: boolean
  fileName?: string
  onProgress?: (progress: RenderProgress) => void
}

export interface AssistantExportResult {
  fileName: string
  savedPath: string
  mimeType: string
  fileSize: number
  fileSizeLabel: string
  duration: number
  downloadTriggered: boolean
}

function getFileExtension(mimeType: string): string {
  const mime = mimeType.toLowerCase()
  if (mime.includes('webm')) return 'webm'
  if (mime.includes('matroska')) return 'mkv'
  if (mime.includes('quicktime') || mime.includes('mov')) return 'mov'
  if (mime.includes('audio/mpeg') || mime.includes('mp3')) return 'mp3'
  if (mime.includes('audio/wav') || mime.includes('wave')) return 'wav'
  if (mime.includes('audio/aac') || mime.includes('adts')) return 'aac'
  return 'mp4'
}

function sanitizeFileName(input: string): string {
  return input.replace(/[\\/:*?"<>|]+/g, '-').trim() || 'assistant-export'
}

function triggerDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  requestIdleCallback(() => URL.revokeObjectURL(url))
}

export async function startAssistantExport(
  request: AssistantExportRequest = {},
): Promise<AssistantExportResult> {
  const timeline = useTimelineStore.getState()
  const project = useProjectStore.getState().currentProject
  if (!project) {
    throw new Error('当前没有已打开的项目，无法导出。')
  }

  const busAudioEq = usePlaybackStore.getState().busAudioEq
  const masterBusDb = usePlaybackStore.getState().masterBusDb
  const defaultSettings: ExtendedExportSettings = {
    codec: 'h264',
    quality: 'high',
    resolution: {
      width: project.metadata.width,
      height: project.metadata.height,
    },
    mode: 'video',
    videoContainer: 'mp4',
    renderWholeProject: true,
  }

  const settings: ExtendedExportSettings = {
    ...defaultSettings,
    ...request.settings,
    resolution: request.settings?.resolution ?? defaultSettings.resolution,
  }

  const renderWholeProject = settings.renderWholeProject ?? true
  const effectiveInPoint = renderWholeProject ? null : timeline.inPoint
  const effectiveOutPoint = renderWholeProject ? null : timeline.outPoint
  const clientSettings = mapToClientSettings(settings, timeline.fps)

  if (settings.mode === 'video' && settings.videoContainer) {
    clientSettings.container = settings.videoContainer
  }
  if (settings.mode === 'audio' && settings.audioContainer) {
    clientSettings.container = settings.audioContainer
    clientSettings.mode = 'audio'
    clientSettings.audioCodec = getDefaultAudioCodec(settings.audioContainer)
    clientSettings.audioBitrate = getAudioBitrateForQuality(settings.quality)
  }
  clientSettings.mode = settings.mode

  if (settings.mode === 'video') {
    const validation = validateSettings(clientSettings)
    if (!validation.valid) {
      throw new Error(validation.error)
    }

    const supportedCodecs = await getSupportedCodecs({
      width: clientSettings.resolution.width,
      height: clientSettings.resolution.height,
      bitrate: clientSettings.videoBitrate,
    })
    if (!supportedCodecs.includes(clientSettings.codec)) {
      const fallbackCodec = selectFallbackVideoCodec(
        supportedCodecs,
        clientSettings.container === 'mp4' ||
          clientSettings.container === 'mov' ||
          clientSettings.container === 'webm' ||
          clientSettings.container === 'mkv'
          ? clientSettings.container
          : undefined,
      )
      if (!fallbackCodec) {
        throw new Error('当前浏览器不支持可用的视频导出编码。')
      }
      clientSettings.codec = fallbackCodec
      clientSettings.container = getPreferredContainerForCodec(fallbackCodec)
    }
  }

  const composition = convertTimelineToComposition(
    timeline.tracks,
    timeline.items,
    timeline.transitions,
    timeline.fps,
    project.metadata.width,
    project.metadata.height,
    effectiveInPoint,
    effectiveOutPoint,
    timeline.keyframes,
    project.metadata.backgroundColor,
    busAudioEq,
    masterBusDb,
  )

  composition.tracks = await resolveMediaUrls(composition.tracks, { useProxy: false })

  const renderResult =
    settings.mode === 'audio'
      ? await renderAudioOnly({
          settings: clientSettings,
          composition,
          signal: request.signal,
          onProgress: request.onProgress,
        })
      : await renderComposition({
          settings: clientSettings,
          composition,
          signal: request.signal,
          onProgress: request.onProgress,
        })

  const resolvedFileName = `${sanitizeFileName(
    request.fileName ?? `${project.name}-${Date.now()}`,
  )}.${getFileExtension(renderResult.mimeType)}`
  const savedFileName = await saveExportFile(project.id, resolvedFileName, renderResult.blob)
  const savedPath = `projects/${project.id}/exports/${savedFileName}`

  if (request.download !== false && typeof document !== 'undefined') {
    triggerDownload(renderResult.blob, savedFileName)
  }

  return {
    fileName: savedFileName,
    savedPath,
    mimeType: renderResult.mimeType,
    fileSize: renderResult.fileSize,
    fileSizeLabel: formatBytes(renderResult.fileSize),
    duration: renderResult.duration,
    downloadTriggered: request.download !== false && typeof document !== 'undefined',
  }
}
